import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Heart, 
  UtensilsCrossed,
  Star,
  MapPin,
  ShoppingBag,
  Loader2,
  RefreshCw,
  Clock,
  ArrowRight,
  Trash2,
  History
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
import { getFullImageUrl } from '../utils/images';
import { getApiErrorMessage } from '../utils/errorHelper';

export default function Favorites() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [favoriteDishes, setFavoriteDishes] = useState([]);
  const [favoriteRestaurants, setFavoriteRestaurants] = useState([]);
  const [orderHistory, setOrderHistory] = useState([]);
  const [reordering, setReordering] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [isAuthenticated, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dishesRes, restaurantsRes, historyRes] = await Promise.all([
        axios.get(`${API}/favorites/dishes`),
        axios.get(`${API}/favorites`),
        axios.get(`${API}/favorites/reorder/history`)
      ]);
      
      setFavoriteDishes(dishesRes.data);
      setFavoriteRestaurants(restaurantsRes.data);
      setOrderHistory(historyRes.data);
    } catch (error) {
      console.error('Error fetching favorites:', error);
      toast.error('Erreur lors du chargement des favoris');
    } finally {
      setLoading(false);
    }
  };

  const removeFavoriteDish = async (dishId) => {
    try {
      await axios.delete(`${API}/favorites/dishes/${dishId}`);
      setFavoriteDishes(prev => prev.filter(d => d.id !== dishId));
      toast.success('Plat retiré des favoris');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const removeFavoriteRestaurant = async (restaurantId) => {
    try {
      await axios.delete(`${API}/favorites/${restaurantId}`);
      setFavoriteRestaurants(prev => prev.filter(r => r.id !== restaurantId));
      toast.success('Restaurant retiré des favoris');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleReorder = async (orderId) => {
    setReordering(orderId);
    try {
      const response = await axios.post(`${API}/favorites/reorder/${orderId}`, {});
      toast.success(response.data.message);
      navigate('/checkout');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de la re-commande'));
    } finally {
      setReordering(null);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bg-gray-50 min-h-screen">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Mes Favoris</h1>
              <p className="text-gray-500 mt-1">Retrouvez vos plats et restaurants préférés</p>
            </div>
            <Button 
              variant="outline" 
              onClick={fetchData}
              className="rounded-full"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="dishes" className="space-y-6">
            <TabsList className="bg-white border border-gray-200 rounded-xl p-1">
              <TabsTrigger 
                value="dishes" 
                className="rounded-lg data-[state=active]:bg-emerald-500 data-[state=active]:text-white"
              >
                <UtensilsCrossed className="w-4 h-4 mr-2" />
                Plats ({favoriteDishes.length})
              </TabsTrigger>
              <TabsTrigger 
                value="restaurants"
                className="rounded-lg data-[state=active]:bg-emerald-500 data-[state=active]:text-white"
              >
                <ShoppingBag className="w-4 h-4 mr-2" />
                Restaurants ({favoriteRestaurants.length})
              </TabsTrigger>
              <TabsTrigger 
                value="history"
                className="rounded-lg data-[state=active]:bg-emerald-500 data-[state=active]:text-white"
              >
                <History className="w-4 h-4 mr-2" />
                Re-commander ({orderHistory.length})
              </TabsTrigger>
            </TabsList>

            {/* Favorite Dishes Tab */}
            <TabsContent value="dishes">
              {favoriteDishes.length === 0 ? (
                <EmptyState 
                  icon={UtensilsCrossed}
                  title="Aucun plat favori"
                  description="Explorez les menus et ajoutez vos plats préférés en cliquant sur le cœur"
                  actionLabel="Rechercher des plats"
                  actionLink="/search"
                />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {favoriteDishes.map((dish) => (
                    <div 
                      key={dish.id}
                      className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-md transition-shadow"
                    >
                      {/* Image */}
                      <div className="relative aspect-[16/10] bg-gray-100">
                        {dish.image ? (
                          <img 
                            src={getFullImageUrl(dish.image)} 
                            alt={dish.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <UtensilsCrossed className="w-12 h-12 text-gray-300" />
                          </div>
                        )}
                        <Badge className="absolute bottom-2 left-2 bg-white text-emerald-600 font-bold shadow-sm">
                          {dish.price?.toFixed(2)} €
                        </Badge>
                        <button
                          onClick={() => removeFavoriteDish(dish.id)}
                          className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                        >
                          <Heart className="w-4 h-4 fill-current" />
                        </button>
                      </div>
                      
                      {/* Content */}
                      <div className="p-4">
                        <h3 className="font-semibold text-gray-900">{dish.name}</h3>
                        {dish.description && (
                          <p className="text-sm text-gray-500 mt-1 line-clamp-2">{dish.description}</p>
                        )}
                        
                        {/* Restaurant Link */}
                        {dish.restaurant && (
                          <Link 
                            to={`/restaurant/${dish.restaurant.id}`}
                            className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100 text-sm text-gray-600 hover:text-emerald-600"
                          >
                            <ShoppingBag className="w-4 h-4" />
                            <span>{dish.restaurant.name}</span>
                            <ArrowRight className="w-4 h-4 ml-auto" />
                          </Link>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Favorite Restaurants Tab */}
            <TabsContent value="restaurants">
              {favoriteRestaurants.length === 0 ? (
                <EmptyState 
                  icon={ShoppingBag}
                  title="Aucun commerce favori"
                  description="Ajoutez des commerces à vos favoris pour les retrouver facilement"
                  actionLabel="Voir les commerces"
                  actionLink="/restaurants"
                />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {favoriteRestaurants.map((restaurant) => (
                    <Link 
                      key={restaurant.id}
                      to={`/restaurant/${restaurant.id}`}
                      className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-md transition-all group"
                    >
                      {/* Cover Image */}
                      <div className="relative aspect-[16/9] bg-gray-100">
                        {restaurant.cover_image ? (
                          <img 
                            src={getFullImageUrl(restaurant.cover_image)} 
                            alt={restaurant.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ShoppingBag className="w-12 h-12 text-gray-300" />
                          </div>
                        )}
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            removeFavoriteRestaurant(restaurant.id);
                          }}
                          className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                        >
                          <Heart className="w-4 h-4 fill-current" />
                        </button>
                      </div>
                      
                      {/* Content */}
                      <div className="p-4">
                        <h3 className="font-semibold text-gray-900 group-hover:text-emerald-600 transition-colors">
                          {restaurant.name}
                        </h3>
                        <div className="flex items-center gap-3 mt-2 text-sm text-gray-500">
                          {restaurant.avg_rating > 0 && (
                            <span className="flex items-center gap-1">
                              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                              {restaurant.avg_rating.toFixed(1)}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {restaurant.city}
                          </span>
                        </div>
                        {restaurant.cuisine_type && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {restaurant.cuisine_type.slice(0, 3).map((type) => (
                              <Badge key={type} variant="secondary" className="text-xs">
                                {type}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Order History / Reorder Tab */}
            <TabsContent value="history">
              {orderHistory.length === 0 ? (
                <EmptyState 
                  icon={History}
                  title="Aucune commande passée"
                  description="Vos commandes terminées apparaîtront ici pour faciliter la re-commande"
                  actionLabel="Commander maintenant"
                  actionLink="/restaurants"
                />
              ) : (
                <div className="space-y-4">
                  {orderHistory.map((order) => (
                    <div 
                      key={order.id}
                      className="bg-white rounded-2xl border border-gray-100 p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between gap-4">
                        {/* Restaurant Info */}
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-14 h-14 rounded-xl overflow-hidden bg-gray-100 shrink-0">
                            {order.restaurant?.cover_image ? (
                              <img 
                                src={getFullImageUrl(order.restaurant.cover_image)} 
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <ShoppingBag className="w-6 h-6 text-gray-300" />
                              </div>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="font-semibold text-gray-900 truncate">
                              {order.restaurant?.name || 'Restaurant'}
                            </h3>
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <Clock className="w-4 h-4" />
                              {new Date(order.created_at).toLocaleDateString('fr-FR', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                              })}
                            </div>
                            <p className="text-sm text-emerald-600 font-medium mt-1">
                              {order.total_amount?.toFixed(2)} € • {order.items?.length || 0} article(s)
                            </p>
                          </div>
                        </div>

                        {/* Reorder Button */}
                        <Button
                          onClick={() => handleReorder(order.id)}
                          disabled={reordering === order.id || !order.restaurant?.is_active}
                          className="bg-emerald-500 hover:bg-emerald-600 rounded-xl shrink-0"
                        >
                          {reordering === order.id ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <RefreshCw className="w-4 h-4 mr-2" />
                          )}
                          Re-commander
                        </Button>
                      </div>

                      {/* Items Preview */}
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <div className="flex flex-wrap gap-2">
                          {order.items?.slice(0, 4).map((item, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {item.quantity}x {item.dish_name}
                            </Badge>
                          ))}
                          {order.items?.length > 4 && (
                            <Badge variant="outline" className="text-xs text-gray-400">
                              +{order.items.length - 4} autre(s)
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </Layout>
  );
}

// Empty State Component
function EmptyState({ icon: Icon, title, description, actionLabel, actionLink }) {
  return (
    <div className="text-center py-16">
      <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
        <Icon className="w-10 h-10 text-gray-400" />
      </div>
      <h3 className="text-xl font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-500 mb-6 max-w-sm mx-auto">{description}</p>
      <Link to={actionLink}>
        <Button className="bg-emerald-500 hover:bg-emerald-600 rounded-full">
          {actionLabel}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </Link>
    </div>
  );
}
